<template>
  <div class="auth-container">
    <div class="auth-card">
      <div v-if="loading" class="auth-header">
        <p>Loading invite...</p>
      </div>
      
      <div v-else-if="error && !invite" class="auth-header">
        <h1>Invalid Invite</h1>
        <p class="error-text">{{ error }}</p>
        <router-link to="/login" class="link">Go to Login</router-link>
      </div>
      
      <template v-else-if="invite">
        <div class="auth-header">
          <h1>Accept Invite</h1>
          <p>Create your account for <strong>{{ invite.email }}</strong></p>
        </div>
        
        <form @submit.prevent="handleAccept" class="auth-form">
          <div class="form-group">
            <label for="name">Your Name</label>
            <input
              id="name"
              v-model="name"
              type="text"
              placeholder="John Doe"
              required
              autofocus
            />
          </div>
          
          <div class="form-group">
            <label for="password">Password</label>
            <input
              id="password"
              v-model="password"
              type="password"
              placeholder="At least 8 characters"
              required
              minlength="8"
            />
          </div>
          
          <div class="form-group">
            <label for="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              v-model="confirmPassword"
              type="password"
              placeholder="••••••••"
              required
            />
          </div>
          
          <div v-if="error" class="error-message">
            {{ error }}
          </div>
          
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? 'Creating account...' : 'Create Account' }}
          </button>
        </form>
      </template>
    </div>
  </div>
</template>

<script>
import { inviteApi } from '../services/api'

export default {
  name: 'AcceptInviteView',
  data() {
    return {
      invite: null,
      name: '',
      password: '',
      confirmPassword: '',
      error: null,
      loading: true,
      submitting: false
    }
  },
  async created() {
    try {
      this.invite = await inviteApi.getInvite(this.$route.params.token)
    } catch (err) {
      this.error = err.message
    } finally {
      this.loading = false
    }
  },
  methods: {
    async handleAccept() {
      if (this.password !== this.confirmPassword) {
        this.error = 'Passwords do not match'
        return
      }
      
      if (this.password.length < 8) {
        this.error = 'Password must be at least 8 characters'
        return
      }
      
      this.error = null
      this.submitting = true
      
      try {
        const data = await inviteApi.acceptInvite(this.$route.params.token, {
          name: this.name,
          password: this.password
        })
        
        // Set auth state
        this.$store.commit('auth/SET_AUTH', {
          accessToken: data.accessToken,
          user: data.user
        })
        
        this.$router.push('/stations')
      } catch (err) {
        this.error = err.message
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.auth-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gray-100);
  padding: 20px;
}

.auth-card {
  background: var(--white);
  border-radius: 12px;
  box-shadow: var(--shadow-md);
  padding: 40px;
  width: 100%;
  max-width: 400px;
}

.auth-header {
  text-align: center;
  margin-bottom: 32px;
}

.auth-header h1 {
  font-size: 24px;
  font-weight: 600;
  color: var(--gray-900);
  margin: 0 0 8px 0;
}

.auth-header p {
  font-size: 14px;
  color: var(--gray-500);
  margin: 0;
}

.error-text {
  color: #dc2626;
}

.link {
  display: inline-block;
  margin-top: 16px;
  color: var(--gray-600);
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: var(--gray-700);
}

.form-group input {
  padding: 10px 12px;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.form-group input:focus {
  outline: none;
  border-color: var(--gray-400);
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
}

.error-message {
  background: #fef2f2;
  color: #dc2626;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 14px;
}

.btn-primary {
  padding: 12px;
  background: var(--accent);
  color: var(--white);
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-primary:hover:not(:disabled) {
  background: var(--accent-hover);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
